import { motion } from "framer-motion";
import { Film, Award, Users } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Showreel from "@/components/Showreel";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";

const StudioAbout = () => {
  return (
    <div className="min-h-screen bg-[#030303] studio-body">
      <SEO
        brand="studio"
        path="/studio/about"
        title="About the Studio"
        description="Wake N' Bake Studio is a cinematic production house. We don't just shoot — we tell stories with film, light, and sound."
      />
      <Navigation brand="studio" />

      {/* Cinematic Showreel Hero */}
      <Showreel
        variant="hero"
        eyebrow="2026 SHOWREEL"
        title={<>WE DON'T JUST SHOOT.<br /><span className="text-[#F97316]">WE TELL STORIES.</span></>}
        subtitle="Wake N' Bake Studio is a creative production house transforming brands into unforgettable cinematic experiences."
      >
        <Link
          to="/studio/portfolio"
          className="inline-block px-8 py-4 bg-[#F97316] text-white font-medium hover:bg-[#EA580C] transition-colors"
          data-testid="studio-hero-portfolio-cta"
        >
          See the Work
        </Link>
      </Showreel>

      {/* Studio Philosophy */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1702126952856-f366d8cb5462?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Film Production"
                className="w-full h-96 object-cover rounded-2xl"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="heading-font text-4xl md:text-5xl text-white mb-6">
                OUR PHILOSOPHY
              </h2>
              <p className="text-lg text-gray-300 mb-4">
                Every brand has a story worth telling. Our job is to find that story and bring it to life in ways that captivate, inspire, and drive action.
              </p>
              <p className="text-lg text-gray-300 mb-4">
                We don't believe in cookie-cutter templates or recycled concepts. Every project is a blank canvas, an opportunity to create something that's never been seen before.
              </p>
              <p className="text-lg text-gray-300">
                From concept to final cut, we obsess over every detail—lighting, composition, sound design, pacing. Because we know that great storytelling lives in the details.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Stand For */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="heading-font text-4xl md:text-5xl text-white text-center mb-16"
          >
            WHAT WE STAND FOR
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Film className="w-12 h-12 text-[#F97316]" />,
                title: "Cinematic Excellence",
                description: "We approach every project with a filmmaker's mindset. High production value isn't optional—it's our standard."
              },
              {
                icon: <Award className="w-12 h-12 text-[#EAB308]" />,
                title: "Creative Fearlessness",
                description: "We push boundaries and take creative risks. Safe is boring. Bold is memorable."
              },
              {
                icon: <Users className="w-12 h-12 text-[#DC2626]" />,
                title: "Authentic Collaboration",
                description: "Your vision matters. We work closely with you to ensure the final product reflects your brand's true essence."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0A0A0A] border border-[#F97316]/30 rounded-none p-8 hover:border-[#F97316] transition-all"
              >
                <div className="mb-4">{item.icon}</div>
                <h3 className="heading-font text-2xl text-white mb-3">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-font text-4xl md:text-5xl text-white mb-6">
              HOW WE WORK
            </h2>
            <p className="text-xl text-gray-300">
              From pre-production to final delivery, we're obsessed with craft.
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              { phase: "PRE-PRODUCTION", details: "Concept development, scriptwriting, storyboarding, location scouting" },
              { phase: "PRODUCTION", details: "Professional cinematography, lighting, sound recording, on-set direction" },
              { phase: "POST-PRODUCTION", details: "Editing, color grading, sound design, motion graphics, VFX" }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-6 bg-[#030303] border border-[#262626] p-6 rounded-none"
              >
                <div className="text-4xl font-bold text-[#F97316]/20 min-w-[80px]">
                  0{index + 1}
                </div>
                <div>
                  <div className="heading-font text-xl text-[#F97316] mb-2">{item.phase}</div>
                  <div className="text-gray-400">{item.details}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-font text-4xl md:text-5xl text-white mb-6">
              READY TO CREATE SOMETHING CINEMATIC?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's bring your vision to life.
            </p>
            <Link
              to="/studio/contact"
              className="inline-block px-8 py-4 bg-[#F97316] text-white rounded-none font-medium hover:bg-[#EA580C] transition-colors"
              data-testid="studio-contact-cta"
            >
              Start a Project
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer brand="studio" />
    </div>
  );
};

export default StudioAbout;